// src/pages/lms/LMSAccessControl.jsx — COMPLETE FIXED
// Fixes: typing lag (uncontrolled refs for text inputs), role not showing,
//        staff count 0, credentials not appearing after save
import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { useLMSAuth } from '../../hooks/useLMSAuth';
import LMSLayout from './LMSLayout';
import { LMS_PAGES, ROLE_TEMPLATES } from '../../hooks/useLMSAccess';
import {
  Plus, X, Trash2, Edit, Eye, EyeOff, Shield,
  Key, Copy, CheckCircle, Lock, Unlock, RefreshCw,
} from 'lucide-react';

const PAGE_KEYS = Object.keys(LMS_PAGES);
const COLORS    = ['#4F8EF7','#7C3AED','#10B981','#F59E0B','#EF4444','#EC4899','#22D3EE','#8B5CF6','#F97316','#64748B'];
const EMOJIS    = ['👤','👨‍🏫','📖','💰','📋','🎓','🏛','⚙️','📊','🔧'];
const INP       = { width:'100%', padding:'9px 12px', borderRadius:8, border:'1.5px solid #E5E7EB', fontSize:13, boxSizing:'border-box', outline:'none', color:'#111827', background:'#fff' };

// ── Credential Form Modal — isolated so typing doesn't re-render parent ──
function CredForm({ roles, institution, account, editCred, onSave, onClose }) {
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');
  const [showPin, setShowPin] = useState(false);
  const [roleId,  setRoleId]  = useState(editCred?.role_id || '');
  const [copied,  setCopied]  = useState(false);

  // Uncontrolled refs for text inputs — fixes typing lag
  const nameRef  = useRef();
  const userRef  = useRef();
  const pinRef   = useRef();
  const phoneRef = useRef();
  const emailRef = useRef();
  const deptRef  = useRef();

  const save = async () => {
    const name = nameRef.current?.value?.trim() || '';
    const user = userRef.current?.value?.trim().toLowerCase() || '';
    const pin  = pinRef.current?.value?.trim() || '';
    if (!roleId)   { setError('Select a role'); return; }
    if (!name)     { setError('Staff name required'); return; }
    if (!user)     { setError('Username required'); return; }
    if (!editCred && !pin) { setError('PIN required'); return; }

    setSaving(true); setError('');
    try {
      const data = {
        institution_id:         institution.id,
        institution_account_id: account.id,
        role_id:                roleId,
        staff_name:             name,
        staff_username:         user,
        staff_phone:            phoneRef.current?.value?.trim() || null,
        staff_email:            emailRef.current?.value?.trim() || null,
        department:             deptRef.current?.value?.trim()  || null,
        is_active:              true,
      };
      if (pin) data.staff_pin = pin;

      if (editCred) {
        const { error: e } = await supabase.from('lms_staff_credentials')
          .update(data).eq('id', editCred.id);
        if (e) throw e;
        onSave('✅ Credentials updated!');
      } else {
        const { error: e } = await supabase.from('lms_staff_credentials').insert(data);
        if (e) throw e;
        const roleName = roles.find(r => r.id === roleId)?.name || '';
        onSave(`✅ Credential created! Share with ${name}: Username: ${user} · PIN: ${pin} · Role: ${roleName}`);
      }
    } catch(e) {
      setError(e.message?.includes('unique')
        ? 'Username already exists. Choose a different one.'
        : (e.message || 'Save failed'));
    } finally { setSaving(false); }
  };

  const roleForPreview = roles.find(r => r.id === roleId);

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div style={{ background:'#fff', borderRadius:20, width:'100%', maxWidth:520, maxHeight:'92vh', overflowY:'auto' }}>
        <div style={{ padding:'20px 24px', borderBottom:'1px solid #E5E7EB', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, background:'#fff', zIndex:1 }}>
          <h3 style={{ margin:0, fontWeight:800, color:'#111827', fontSize:18 }}>{editCred ? 'Edit Credential' : 'Add Staff Credential'}</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer' }}><X size={22} color="#6B7280"/></button>
        </div>
        <div style={{ padding:24 }}>
          {error && <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:8, padding:'10px', marginBottom:14, fontSize:13, color:'#991B1B', fontWeight:600 }}>{error}</div>}

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
            {/* Role */}
            <div style={{ gridColumn:'span 2' }}>
              <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:5 }}>Assign Role *</label>
              <select value={roleId} onChange={e => setRoleId(e.target.value)} style={INP}>
                <option value="">Select Role...</option>
                {roles.filter(r => !r.is_admin).map(r => (
                  <option key={r.id} value={r.id}>{r.emoji} {r.name}</option>
                ))}
              </select>
            </div>

            {/* Name */}
            <div style={{ gridColumn:'span 2' }}>
              <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:5 }}>Staff Full Name *</label>
              <input ref={nameRef} defaultValue={editCred?.staff_name || ''} placeholder="e.g. Mr. Ahmed Ali" style={INP}/>
            </div>

            {/* Username */}
            <div>
              <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:5 }}>Username *</label>
              <input ref={userRef} defaultValue={editCred?.staff_username || ''} placeholder="e.g. ahmed_teacher" style={{ ...INP, fontFamily:'monospace' }}/>
            </div>

            {/* PIN */}
            <div>
              <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:5 }}>
                {editCred ? 'New PIN (blank = keep old)' : 'PIN *'}
              </label>
              <div style={{ position:'relative' }}>
                <input ref={pinRef} type={showPin ? 'text' : 'password'} defaultValue=""
                  placeholder={editCred ? 'Leave blank to keep' : '4-8 digit PIN'}
                  style={{ ...INP, paddingRight:38 }}/>
                <button type="button" onClick={() => setShowPin(s => !s)}
                  style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer' }}>
                  {showPin ? <EyeOff size={16} color="#9CA3AF"/> : <Eye size={16} color="#9CA3AF"/>}
                </button>
              </div>
            </div>

            {/* Phone */}
            <div>
              <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:5 }}>Phone</label>
              <input ref={phoneRef} defaultValue={editCred?.staff_phone || ''} placeholder="03xx-xxxxxxx" style={INP}/>
            </div>

            {/* Email */}
            <div>
              <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:5 }}>Email</label>
              <input ref={emailRef} defaultValue={editCred?.staff_email || ''} placeholder="staff@email.com" style={INP}/>
            </div>

            {/* Department */}
            <div style={{ gridColumn:'span 2' }}>
              <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:5 }}>Department</label>
              <input ref={deptRef} defaultValue={editCred?.department || ''} placeholder="e.g. Computer Science" style={INP}/>
            </div>

            {/* WhatsApp preview with actual credentials */}
            {!editCred && roleForPreview && (
              <div style={{ gridColumn:'span 2', background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:12, padding:14 }}>
                <div style={{ fontWeight:700, color:'#065F46', fontSize:13, marginBottom:8 }}>📲 Share via WhatsApp</div>
                <div style={{ fontSize:12, color:'#374151', background:'#fff', padding:10, borderRadius:8, border:'1px solid #D1FAE5', fontFamily:'monospace', whiteSpace:'pre-wrap', lineHeight:1.8 }}>{`🏫 UNIVERSE LMS Staff Login
━━━━━━━━━━━━━━━━━━━━
Name:       ${nameRef.current?.value || '(staff name)'}
Username:   ${userRef.current?.value || '(username)'}
PIN:        ${pinRef.current?.value  || '(pin)'}
Role:       ${roleForPreview.emoji} ${roleForPreview.name}

Login at: /lms/staff-login
Select institution → Enter username → Enter PIN
━━━━━━━━━━━━━━━━━━━━`}</div>
                <button
                  onClick={() => {
                    const msg = `🏫 UNIVERSE LMS Staff Login
━━━━━━━━━━━━━━━━━━━━
Name:       ${nameRef.current?.value || ''}
Username:   ${userRef.current?.value || ''}
PIN:        ${pinRef.current?.value  || ''}
Role:       ${roleForPreview.emoji} ${roleForPreview.name}

Login at: yourportal.com/lms/staff-login
Select institution → Enter username → Enter PIN
━━━━━━━━━━━━━━━━━━━━`;
                    navigator.clipboard.writeText(msg);
                    setCopied('wa');
                    setTimeout(() => setCopied(''), 2000);
                  }}
                  style={{ marginTop:8, display:'flex', alignItems:'center', gap:6, padding:'7px 14px', borderRadius:7, border:'none', background:'#10B981', color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer' }}>
                  {copied==='wa' ? '✅ Copied!' : '📋 Copy Message'}
                </button>
              </div>
            )}
          </div>

          <div style={{ display:'flex', gap:10, marginTop:20 }}>
            <button onClick={onClose} style={{ flex:1, padding:'12px 0', borderRadius:10, border:'1.5px solid #E5E7EB', background:'#fff', color:'#374151', fontWeight:600, cursor:'pointer' }}>Cancel</button>
            <button onClick={save} disabled={saving}
              style={{ flex:2, padding:'12px 0', borderRadius:10, border:'none', background: saving ? '#E5E7EB' : 'linear-gradient(135deg,#10B981,#059669)', color: saving ? '#9CA3AF' : '#fff', fontWeight:700, cursor: saving ? 'wait' : 'pointer' }}>
              {saving ? 'Saving...' : editCred ? 'Update Credential' : 'Create Credential'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Role Form Modal — also isolated ──────────────────
function RoleFormModal({ editRole, institution, account, onSave, onClose }) {
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');
  const [color,  setColor]  = useState(editRole?.color  || '#4F8EF7');
  const [emoji,  setEmoji]  = useState(editRole?.emoji  || '👤');
  const [pages,  setPages]  = useState(() => {
    const base = Object.fromEntries(PAGE_KEYS.map(k => [k, { can_access:false, can_edit:false, can_view_only:false }]));
    if (editRole?.lms_staff_page_access) {
      for (const pa of editRole.lms_staff_page_access) {
        if (base[pa.page_key]) base[pa.page_key] = { can_access: pa.can_access, can_edit: pa.can_edit, can_view_only: pa.can_view_only };
      }
    }
    return base;
  });

  const nameRef = useRef();
  const descRef = useRef();

  const applyTemplate = (key) => {
    const t = ROLE_TEMPLATES[key];
    if (!t) return;
    setColor(t.color); setEmoji(t.emoji);
    if (nameRef.current) nameRef.current.value = t.name;
    setPages(Object.fromEntries(PAGE_KEYS.map(k => [k, {
      can_access:    t.pages.includes(k),
      can_edit:      t.pages.includes(k),
      can_view_only: false,
    }])));
  };

  const setPage = (key, can_access, can_edit, can_view_only) => {
    setPages(p => ({ ...p, [key]: { can_access, can_edit, can_view_only } }));
  };

  const selectAll = (type) => {
    setPages(Object.fromEntries(PAGE_KEYS.map(k => [k, {
      can_access: true, can_edit: type === 'edit', can_view_only: type === 'view',
    }])));
  };

  const clearAll = () => {
    setPages(Object.fromEntries(PAGE_KEYS.map(k => [k, { can_access:false, can_edit:false, can_view_only:false }])));
  };

  const save = async () => {
    const name = nameRef.current?.value?.trim() || '';
    const desc = descRef.current?.value?.trim() || '';
    if (!name) { setError('Role name required'); return; }
    if (!Object.values(pages).some(p => p.can_access)) { setError('Select at least one page'); return; }

    setSaving(true); setError('');
    try {
      let roleId;
      if (editRole) {
        const { error: e } = await supabase.from('lms_staff_roles')
          .update({ name, description: desc, color, emoji }).eq('id', editRole.id);
        if (e) throw e;
        roleId = editRole.id;
        await supabase.from('lms_staff_page_access').delete().eq('role_id', roleId);
      } else {
        const { data: r, error: e } = await supabase.from('lms_staff_roles').insert({
          name, description: desc, color, emoji,
          institution_id: institution.id,
          institution_account_id: account.id,
          is_admin: false,
        }).select().single();
        if (e) throw e;
        roleId = r.id;
      }

      const rows = Object.entries(pages)
        .filter(([, v]) => v.can_access)
        .map(([pageKey, v]) => ({
          institution_id: institution.id,
          role_id: roleId,
          page_key: pageKey,
          can_access: v.can_access,
          can_edit: v.can_edit,
          can_view_only: v.can_view_only,
        }));
      if (rows.length > 0) {
        const { error: e } = await supabase.from('lms_staff_page_access').insert(rows);
        if (e) throw e;
      }
      onSave(editRole ? '✅ Role updated!' : '✅ Role created!');
    } catch(e) { setError(e.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div style={{ background:'#fff', borderRadius:20, width:'100%', maxWidth:700, maxHeight:'94vh', overflowY:'auto' }}>
        <div style={{ padding:'20px 24px', borderBottom:'1px solid #E5E7EB', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, background:'#fff', zIndex:1 }}>
          <h3 style={{ margin:0, fontWeight:800, color:'#111827', fontSize:18 }}>{editRole ? 'Edit Role' : 'Create New Role'}</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer' }}><X size={22} color="#6B7280"/></button>
        </div>
        <div style={{ padding:24 }}>
          {error && <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:8, padding:'10px', marginBottom:14, fontSize:13, color:'#991B1B', fontWeight:600 }}>{error}</div>}

          {/* Templates */}
          {!editRole && (
            <div style={{ marginBottom:20 }}>
              <div style={{ fontSize:11, fontWeight:700, color:'#9CA3AF', letterSpacing:0.5, marginBottom:8 }}>START FROM TEMPLATE</div>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                {Object.entries(ROLE_TEMPLATES).map(([key, t]) => (
                  <button key={key} onClick={() => applyTemplate(key)}
                    style={{ padding:'7px 14px', borderRadius:8, border:`1.5px solid ${t.color}40`, background:`${t.color}10`, color:t.color, fontSize:12, fontWeight:700, cursor:'pointer' }}>
                    {t.emoji} {t.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Name + Desc */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:16 }}>
            <div style={{ gridColumn:'span 2' }}>
              <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:5 }}>Role Name *</label>
              <input ref={nameRef} defaultValue={editRole?.name || ''} placeholder="e.g. Teacher, Principal, Librarian" style={INP}/>
            </div>
            <div style={{ gridColumn:'span 2' }}>
              <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:5 }}>Description</label>
              <input ref={descRef} defaultValue={editRole?.description || ''} placeholder="Brief description..." style={INP}/>
            </div>
            {/* Emoji */}
            <div>
              <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:5 }}>Emoji</label>
              <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                {EMOJIS.map(em => (
                  <button key={em} onClick={() => setEmoji(em)}
                    style={{ width:34, height:34, borderRadius:8, border:`2px solid ${emoji===em?'#4F8EF7':'#E5E7EB'}`, background:emoji===em?'#EFF6FF':'#fff', fontSize:18, cursor:'pointer' }}>
                    {em}
                  </button>
                ))}
              </div>
            </div>
            {/* Color */}
            <div>
              <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:5 }}>Color</label>
              <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                {COLORS.map(c => (
                  <div key={c} onClick={() => setColor(c)}
                    style={{ width:28, height:28, borderRadius:'50%', background:c, cursor:'pointer', border:color===c?'3px solid #111827':'3px solid transparent' }}/>
                ))}
              </div>
            </div>
          </div>

          {/* Page permissions */}
          <div style={{ borderTop:'1px solid #E5E7EB', paddingTop:20 }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14, flexWrap:'wrap', gap:8 }}>
              <div style={{ fontWeight:800, color:'#111827', fontSize:14 }}>🔐 Page Permissions</div>
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={() => selectAll('edit')} style={{ padding:'5px 12px', borderRadius:6, border:'1px solid #BBF7D0', background:'#F0FDF4', color:'#065F46', fontSize:11, fontWeight:700, cursor:'pointer' }}>✅ All Full</button>
                <button onClick={() => selectAll('view')} style={{ padding:'5px 12px', borderRadius:6, border:'1px solid #BFDBFE', background:'#EFF6FF', color:'#1D4ED8', fontSize:11, fontWeight:700, cursor:'pointer' }}>👁 All View</button>
                <button onClick={clearAll}               style={{ padding:'5px 12px', borderRadius:6, border:'1px solid #FECACA', background:'#FEF2F2', color:'#991B1B', fontSize:11, fontWeight:700, cursor:'pointer' }}>❌ Clear</button>
              </div>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 90px 90px 90px', gap:4, padding:'0 8px', marginBottom:6 }}>
              <div style={{ fontSize:11, fontWeight:700, color:'#9CA3AF' }}>Page</div>
              <div style={{ fontSize:11, fontWeight:700, color:'#10B981', textAlign:'center' }}>Full</div>
              <div style={{ fontSize:11, fontWeight:700, color:'#4F8EF7', textAlign:'center' }}>View</div>
              <div style={{ fontSize:11, fontWeight:700, color:'#EF4444', textAlign:'center' }}>None</div>
            </div>

            <div style={{ border:'1px solid #E5E7EB', borderRadius:12, overflow:'hidden' }}>
              {PAGE_KEYS.map((key, i) => {
                const pg  = LMS_PAGES[key];
                const acc = pages[key];
                return (
                  <div key={key} style={{ display:'grid', gridTemplateColumns:'1fr 90px 90px 90px', alignItems:'center', padding:'10px 12px', borderBottom:i<PAGE_KEYS.length-1?'1px solid #F3F4F6':'none', background:acc.can_access?'#FAFFFE':'#FAFAFA' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <span style={{ fontSize:16 }}>{pg.icon}</span>
                      <div>
                        <div style={{ fontSize:13, fontWeight:600, color:'#111827' }}>{pg.label}</div>
                        <div style={{ fontSize:10, color:'#9CA3AF' }}>{pg.description}</div>
                      </div>
                    </div>
                    <div style={{ textAlign:'center' }}>
                      <input type="radio" name={`page-${key}`}
                        checked={acc.can_access && acc.can_edit}
                        onChange={() => setPage(key, true, true, false)}
                        style={{ width:16, height:16, accentColor:'#10B981', cursor:'pointer' }}/>
                    </div>
                    <div style={{ textAlign:'center' }}>
                      <input type="radio" name={`page-${key}`}
                        checked={acc.can_access && !acc.can_edit}
                        onChange={() => setPage(key, true, false, true)}
                        style={{ width:16, height:16, accentColor:'#4F8EF7', cursor:'pointer' }}/>
                    </div>
                    <div style={{ textAlign:'center' }}>
                      <input type="radio" name={`page-${key}`}
                        checked={!acc.can_access}
                        onChange={() => setPage(key, false, false, false)}
                        style={{ width:16, height:16, accentColor:'#EF4444', cursor:'pointer' }}/>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{ display:'flex', gap:10, marginTop:20 }}>
            <button onClick={onClose} style={{ flex:1, padding:'12px 0', borderRadius:10, border:'1.5px solid #E5E7EB', background:'#fff', color:'#374151', fontWeight:600, cursor:'pointer' }}>Cancel</button>
            <button onClick={save} disabled={saving}
              style={{ flex:2, padding:'12px 0', borderRadius:10, border:'none', background: saving ? '#E5E7EB' : 'linear-gradient(135deg,#7C3AED,#4F8EF7)', color: saving ? '#9CA3AF' : '#fff', fontWeight:700, cursor: saving ? 'wait' : 'pointer' }}>
              {saving ? 'Saving...' : editRole ? 'Update Role' : 'Create Role'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── MAIN ──────────────────────────────────────────────
export default function LMSAccessControl() {
  const { account, institution } = useLMSAuth();
  const [roles,   setRoles]   = useState([]);
  const [creds,   setCreds]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [view,    setView]    = useState('roles');
  const [showRoleForm, setShowRoleForm] = useState(false);
  const [showCredForm, setShowCredForm] = useState(false);
  const [editRole, setEditRole] = useState(null);
  const [editCred, setEditCred] = useState(null);
  const [success,  setSuccess] = useState('');
  const [error,    setError]   = useState('');
  const [copied,   setCopied]  = useState('');

  useEffect(() => { if (institution?.id) loadAll(); }, [institution?.id]);

  const loadAll = async () => {
    setLoading(true);
    try {
      // Plain selects — NO FK joins
      const [rRes, paRes, cRes] = await Promise.all([
        supabase.from('lms_staff_roles').select('*')
          .eq('institution_id', institution.id).order('name'),
        supabase.from('lms_staff_page_access').select('*')
          .eq('institution_id', institution.id),
        supabase.from('lms_staff_credentials').select('*')
          .eq('institution_id', institution.id).order('staff_name'),
      ]);

      const paData = paRes.data || [];
      const rData  = rRes.data  || [];
      const cData  = cRes.data  || [];

      // Attach page access to roles
      const rolesWithPA = rData.map(r => ({
        ...r,
        lms_staff_page_access: paData.filter(p => p.role_id === r.id),
      }));

      // Build roles map for credential display
      const rolesMap = {};
      for (const r of rData) rolesMap[r.id] = r;

      // Attach role to credentials
      const credsWithRole = cData.map(c => ({
        ...c,
        role: rolesMap[c.role_id] || null,  // use .role not .lms_staff_roles
      }));

      setRoles(rolesWithPA);
      setCreds(credsWithRole);
    } catch(e) {
      console.error('loadAll error:', e);
      setError('Failed to load: ' + e.message);
    } finally { setLoading(false); }
  };

  const onSaved = async (msg) => {
    setSuccess(msg);
    setShowRoleForm(false); setShowCredForm(false);
    setEditRole(null); setEditCred(null);
    setTimeout(() => setSuccess(''), 8000);
    await loadAll(); // reload to show updated data
  };

  const deleteRole = async (id) => {
    if (!window.confirm('Delete this role? All credentials with this role will also lose their role.')) return;
    await supabase.from('lms_staff_roles').delete().eq('id', id);
    await loadAll();
  };

  const deleteCred = async (id) => {
    if (!window.confirm('Delete this staff credential?')) return;
    await supabase.from('lms_staff_credentials').delete().eq('id', id);
    await loadAll();
  };

  const toggleActive = async (cred) => {
    await supabase.from('lms_staff_credentials')
      .update({ is_active: !cred.is_active }).eq('id', cred.id);
    await loadAll();
  };

  const copyText = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(''), 2000);
  };

  return (
    <LMSLayout>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20, flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 style={{ fontSize:22, fontWeight:800, color:'#111827', margin:0 }}>Access Control</h1>
          <p style={{ color:'#6B7280', fontSize:13, margin:'4px 0 0' }}>Create roles, set page permissions, generate staff credentials</p>
        </div>
        <div style={{ display:'flex', gap:10 }}>
          <button onClick={loadAll} style={{ display:'flex', alignItems:'center', gap:6, padding:'9px 14px', borderRadius:9, border:'1px solid #E5E7EB', background:'#fff', color:'#374151', fontSize:13, fontWeight:600, cursor:'pointer' }}>
            <RefreshCw size={14}/> Refresh
          </button>
          {view === 'roles' && (
            <button onClick={() => { setEditRole(null); setShowRoleForm(true); }}
              style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 18px', borderRadius:10, border:'none', background:'linear-gradient(135deg,#7C3AED,#4F8EF7)', color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer' }}>
              <Plus size={16}/> Create Role
            </button>
          )}
          {view === 'credentials' && (
            <button onClick={() => { setEditCred(null); setShowCredForm(true); }}
              style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 18px', borderRadius:10, border:'none', background:'linear-gradient(135deg,#10B981,#059669)', color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer' }}>
              <Plus size={16}/> Add Staff
            </button>
          )}
        </div>
      </div>

      {success && <div style={{ background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:10, padding:'12px 16px', marginBottom:16, fontSize:13, color:'#065F46', fontWeight:700 }}>{success}</div>}
      {error   && <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:10, padding:'12px 16px', marginBottom:16, fontSize:13, color:'#991B1B' }}>{error}<button onClick={()=>setError('')} style={{ float:'right', background:'none', border:'none', cursor:'pointer' }}>✕</button></div>}

      {/* Info */}
      <div style={{ background:'linear-gradient(135deg,#EFF6FF,#F5F3FF)', border:'1px solid #BFDBFE', borderRadius:14, padding:'14px 18px', marginBottom:20 }}>
        <div style={{ fontWeight:700, color:'#1D4ED8', fontSize:13, marginBottom:8 }}>🔐 How Access Control Works</div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))', gap:10, fontSize:12, color:'#374151' }}>
          {[
            { n:'1', t:'Create Role', d:'e.g. Teacher, Principal — choose which pages they see' },
            { n:'2', t:'Set Permissions', d:'Full access, View only, or No access per page' },
            { n:'3', t:'Add Credentials', d:'Username + PIN for each staff member' },
            { n:'4', t:'Share & Login', d:'Staff goes to /lms/staff-login, selects institution, enters username + PIN' },
          ].map(s => (
            <div key={s.n} style={{ display:'flex', gap:10 }}>
              <div style={{ width:22, height:22, borderRadius:'50%', background:'#4F8EF7', color:'#fff', fontSize:11, fontWeight:800, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>{s.n}</div>
              <div><div style={{ fontWeight:700, color:'#1E293B' }}>{s.t}</div><div style={{ color:'#6B7280', fontSize:11 }}>{s.d}</div></div>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', background:'#F1F5F9', borderRadius:12, padding:4, marginBottom:20, width:'fit-content', gap:2 }}>
        {[
          { key:'roles',       label:`🎭 Roles (${roles.length})`             },
          { key:'credentials', label:`🔑 Staff (${creds.length})`             },
        ].map(tab => (
          <button key={tab.key} onClick={() => setView(tab.key)}
            style={{ padding:'8px 20px', borderRadius:9, border:'none', background:view===tab.key?'#fff':'transparent', color:view===tab.key?'#1E293B':'#64748B', fontWeight:700, fontSize:13, cursor:'pointer', boxShadow:view===tab.key?'0 1px 4px rgba(0,0,0,0.08)':'none' }}>
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign:'center', padding:48, color:'#9CA3AF' }}>Loading...</div>
      ) : (
        <>
          {/* ── ROLES ── */}
          {view === 'roles' && (
            roles.length === 0 ? (
              <div style={{ textAlign:'center', padding:60, background:'#fff', borderRadius:16, border:'1px solid #E5E7EB' }}>
                <Shield size={48} color="#E5E7EB" style={{ marginBottom:12 }}/>
                <div style={{ fontSize:15, fontWeight:700, color:'#374151', marginBottom:6 }}>No roles yet</div>
                <div style={{ fontSize:13, color:'#9CA3AF', marginBottom:16 }}>Create your first role to start controlling access</div>
                <div style={{ display:'flex', gap:8, justifyContent:'center', flexWrap:'wrap' }}>
                  {Object.entries(ROLE_TEMPLATES).map(([key, t]) => (
                    <button key={key} onClick={() => { setShowRoleForm(true); setEditRole(null); }}
                      style={{ padding:'8px 14px', borderRadius:8, border:`1px solid ${t.color}40`, background:`${t.color}10`, color:t.color, fontSize:12, fontWeight:700, cursor:'pointer' }}>
                      {t.emoji} {t.name}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:16 }}>
                {roles.map(role => {
                  const pageCount  = (role.lms_staff_page_access||[]).filter(p=>p.can_access).length;
                  const staffCount = creds.filter(c=>c.role_id===role.id).length;
                  return (
                    <div key={role.id} style={{ background:'#fff', borderRadius:16, border:`1.5px solid ${role.color||'#4F8EF7'}30`, overflow:'hidden' }}>
                      <div style={{ padding:'16px 18px', background:`${role.color||'#4F8EF7'}0D`, borderBottom:'1px solid #F3F4F6', display:'flex', alignItems:'center', gap:12 }}>
                        <div style={{ width:46, height:46, borderRadius:12, background:`${role.color||'#4F8EF7'}18`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, flexShrink:0 }}>
                          {role.emoji||'👤'}
                        </div>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontWeight:800, color:'#111827', fontSize:15 }}>{role.name}</div>
                          {role.description && <div style={{ fontSize:12, color:'#6B7280', marginTop:2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{role.description}</div>}
                        </div>
                        <div style={{ display:'flex', gap:6, flexShrink:0 }}>
                          <button onClick={() => { setEditRole(role); setShowRoleForm(true); }}
                            style={{ padding:'6px 10px', borderRadius:7, border:'1px solid #E5E7EB', background:'#F9FAFB', cursor:'pointer' }}>
                            <Edit size={13} color="#374151"/>
                          </button>
                          <button onClick={() => deleteRole(role.id)}
                            style={{ padding:'6px 10px', borderRadius:7, border:'1px solid #FECACA', background:'#FEF2F2', cursor:'pointer' }}>
                            <Trash2 size={13} color="#EF4444"/>
                          </button>
                        </div>
                      </div>
                      <div style={{ padding:'12px 18px' }}>
                        <div style={{ display:'flex', gap:16, fontSize:12, color:'#6B7280', marginBottom:10 }}>
                          <span>🔓 <strong>{pageCount}</strong> pages</span>
                          <span>👤 <strong>{staffCount}</strong> staff member{staffCount!==1?'s':''}</span>
                        </div>
                        <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                          {(role.lms_staff_page_access||[]).filter(p=>p.can_access).slice(0,8).map(pa => (
                            <span key={pa.page_key} style={{ fontSize:10, fontWeight:600, color:role.color||'#4F8EF7', background:`${role.color||'#4F8EF7'}12`, padding:'2px 8px', borderRadius:4 }}>
                              {LMS_PAGES[pa.page_key]?.icon} {LMS_PAGES[pa.page_key]?.label || pa.page_key}
                              {pa.can_view_only ? ' 👁' : ''}
                            </span>
                          ))}
                          {(role.lms_staff_page_access||[]).filter(p=>p.can_access).length > 8 && (
                            <span style={{ fontSize:10, color:'#9CA3AF' }}>+{(role.lms_staff_page_access||[]).filter(p=>p.can_access).length - 8} more</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          )}

          {/* ── CREDENTIALS ── */}
          {view === 'credentials' && (
            creds.length === 0 ? (
              <div style={{ textAlign:'center', padding:60, background:'#fff', borderRadius:16, border:'1px solid #E5E7EB' }}>
                <Key size={48} color="#E5E7EB" style={{ marginBottom:12 }}/>
                <div style={{ fontSize:15, fontWeight:700, color:'#374151', marginBottom:6 }}>No staff credentials yet</div>
                <div style={{ fontSize:13, color:'#9CA3AF' }}>Create roles first, then add staff credentials</div>
              </div>
            ) : (
              <div style={{ background:'#fff', borderRadius:16, border:'1px solid #E5E7EB', overflow:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', minWidth:600 }}>
                  <thead>
                    <tr style={{ background:'#F8FAFF', borderBottom:'1px solid #E5E7EB' }}>
                      {['Staff Member','Role','Username','Contact','Status','Actions'].map(h => (
                        <th key={h} style={{ padding:'12px 16px', textAlign:'left', fontSize:11, fontWeight:800, color:'#6B7280' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {creds.map((c, i) => {
                      const role = c.role; // uses .role (not .lms_staff_roles)
                      return (
                        <tr key={c.id} style={{ borderBottom:i<creds.length-1?'1px solid #F1F5F9':'none', opacity:c.is_active?1:0.5 }}>
                          <td style={{ padding:'13px 16px' }}>
                            <div style={{ fontWeight:700, color:'#111827', fontSize:13 }}>{c.staff_name}</div>
                            {c.department && <div style={{ fontSize:11, color:'#9CA3AF' }}>{c.department}</div>}
                          </td>
                          <td style={{ padding:'13px 16px' }}>
                            {role ? (
                              <span style={{ fontSize:12, fontWeight:700, color:role.color||'#374151', background:`${role.color||'#374151'}15`, padding:'3px 10px', borderRadius:6 }}>
                                {role.emoji} {role.name}
                              </span>
                            ) : (
                              <span style={{ fontSize:12, color:'#9CA3AF' }}>No role</span>
                            )}
                          </td>
                          <td style={{ padding:'13px 16px' }}>
                            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                              <code style={{ fontSize:13, fontWeight:800, color:'#4F8EF7', background:'#EFF6FF', padding:'3px 10px', borderRadius:5 }}>{c.staff_username}</code>
                              <button onClick={() => copyText(c.staff_username, `u-${c.id}`)}
                                style={{ background:'none', border:'none', cursor:'pointer', padding:4 }}>
                                {copied===`u-${c.id}` ? <CheckCircle size={14} color="#10B981"/> : <Copy size={14} color="#94A3B8"/>}
                              </button>
                            </div>
                          </td>
                          <td style={{ padding:'13px 16px' }}>
                            {c.staff_phone && <div style={{ fontSize:12, color:'#374151' }}>📞 {c.staff_phone}</div>}
                            {c.staff_email && <div style={{ fontSize:11, color:'#9CA3AF' }}>{c.staff_email}</div>}
                            {!c.staff_phone && !c.staff_email && <span style={{ fontSize:12, color:'#D1D5DB' }}>—</span>}
                          </td>
                          <td style={{ padding:'13px 16px' }}>
                            <button onClick={() => toggleActive(c)}
                              style={{ display:'flex', alignItems:'center', gap:5, padding:'5px 10px', borderRadius:6, border:`1px solid ${c.is_active?'#BBF7D0':'#FECACA'}`, background:c.is_active?'#F0FDF4':'#FEF2F2', color:c.is_active?'#065F46':'#991B1B', fontSize:11, fontWeight:700, cursor:'pointer' }}>
                              {c.is_active ? <><Unlock size={11}/> Active</> : <><Lock size={11}/> Inactive</>}
                            </button>
                          </td>
                          <td style={{ padding:'13px 14px' }}>
                            <div style={{ display:'flex', gap:6 }}>
                              <button onClick={() => { setEditCred(c); setShowCredForm(true); }}
                                style={{ padding:'6px 10px', borderRadius:7, border:'1px solid #E5E7EB', background:'#F9FAFB', cursor:'pointer' }}>
                                <Edit size={13} color="#374151"/>
                              </button>
                              <button onClick={() => deleteCred(c.id)}
                                style={{ padding:'6px 10px', borderRadius:7, border:'1px solid #FECACA', background:'#FEF2F2', cursor:'pointer' }}>
                                <Trash2 size={13} color="#EF4444"/>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          )}
        </>
      )}

      {/* Modals */}
      {showRoleForm && (
        <RoleFormModal
          key={editRole?.id || 'new-role'}
          editRole={editRole}
          institution={institution}
          account={account}
          onSave={onSaved}
          onClose={() => { setShowRoleForm(false); setEditRole(null); }}
        />
      )}
      {showCredForm && (
        <CredForm
          key={editCred?.id || 'new-cred'}
          editCred={editCred}
          roles={roles}
          institution={institution}
          account={account}
          onSave={onSaved}
          onClose={() => { setShowCredForm(false); setEditCred(null); }}
        />
      )}
    </LMSLayout>
  );
}